// ============================================================================
// CHANGO V5.0 — Customer Groups Manager
// ============================================================================
// Manage customer groups (客户分组). Business users should only deal with the
// visible group name; the immutable DB business key is generated internally.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, HttpError } from '../../api/client.js';

const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

function buildInternalCode(name) {
  const base = String(name || '')
    .trim()
    .replace(/[\s/\\?%*:|"<>]+/g, '-')
    .slice(0, 22) || 'group';
  return `${base}-${Date.now().toString(36)}`.slice(0, 40);
}

function friendlyError(err) {
  const code = err?.code || '';
  const message = err?.message || '';
  if (/DUPLICATE|CONFLICT/i.test(code) || /duplicate key/i.test(message)) {
    return '分组名称已存在，请换一个更具体的名称，比如“20260425渔具展”。';
  }
  if (code === 'GROUP_IN_USE') {
    return message || '这个分组下还有客户，先调整客户分组后再归档。';
  }
  if (code === 'VALIDATION_ERROR') {
    return message || '请检查分组名称和颜色。';
  }
  return message || '操作失败，请稍后重试。';
}

export default function CustomerGroupsManager() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalError, setModalError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    name: '',
    color: '#3b82f6'
  });

  const fetchGroups = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/api/customer-groups');
      setGroups(res?.items || []);
    } catch (err) {
      if (!(err instanceof HttpError && err.status === 404)) {
        setError(friendlyError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const handleCreateClick = () => {
    setError(null);
    setModalError(null);
    setForm({ name: '', color: '#3b82f6' });
    setEditingId(null);
    setShowModal(true);
  };

  const handleEditClick = (group) => {
    setError(null);
    setModalError(null);
    setForm({
      name: group.name,
      color: group.color || '#3b82f6'
    });
    setEditingId(group.id);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setForm({ name: '', color: '#3b82f6' });
    setEditingId(null);
    setModalError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      setModalError('请填写分组名称。');
      return;
    }

    if (!HEX_COLOR_PATTERN.test(form.color)) {
      setModalError('颜色必须是 #RRGGBB 格式。');
      return;
    }

    try {
      setModalError(null);
      if (editingId) {
        await api.patch(`/api/customer-groups/${editingId}`, {
          name: form.name.trim(),
          color: form.color
        });
      } else {
        await api.post('/api/customer-groups', {
          code: buildInternalCode(form.name),
          name: form.name.trim(),
          color: form.color
        });
      }
      await fetchGroups();
      handleModalClose();
    } catch (err) {
      setModalError(friendlyError(err));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('确认归档这个客户分组？分组下还有客户时不能归档。')) {
      return;
    }

    try {
      await api.delete(`/api/customer-groups/${id}`);
      setGroups(groups.filter((g) => g.id !== id));
    } catch (err) {
      setError(friendlyError(err));
    }
  };

  return (
    <div className="px-6 py-6 max-w-4xl mx-auto">
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">客户分组</h1>
          <p className="mt-1 text-sm text-slate-600">
            按展会、渠道、客户类型或重点程度管理客户，便于筛选和后续报价。
          </p>
        </div>
        <button onClick={handleCreateClick} className="btn-primary">
          新建分组
        </button>
      </header>

      {error && (
        <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-800">
          {error}
        </div>
      )}

      <div className="card overflow-hidden">
        {loading && (
          <div className="px-4 py-8 text-center text-slate-500">加载中...</div>
        )}

        {!loading && groups.length === 0 && (
          <div className="px-4 py-8 text-center text-slate-500">
            还没有客户分组 ·{' '}
            <button onClick={handleCreateClick} className="text-chango-700 hover:underline">
              新建一个
            </button>
          </div>
        )}

        {!loading && groups.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">分组名称</th>
                <th className="px-4 py-3">颜色</th>
                <th className="px-4 py-3 text-right">操作</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <tr key={group.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{group.name}</td>
                  <td className="px-4 py-3">
                    {group.color && (
                      <span className="inline-flex items-center gap-2">
                        <span
                          className="h-6 w-6 rounded border border-slate-300"
                          style={{ backgroundColor: group.color }}
                        />
                        <code className="text-xs text-slate-500">{group.color}</code>
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleEditClick(group)}
                      className="mr-3 text-xs text-chango-700 hover:underline"
                    >
                      编辑
                    </button>
                    <button
                      onClick={() => handleDelete(group.id)}
                      className="text-xs text-rose-600 hover:underline"
                    >
                      归档
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-4">
        <Link to="/app/customers" className="text-sm text-slate-600 hover:underline">
          返回客户列表
        </Link>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
            <h2 className="mb-4 text-lg font-semibold text-slate-900">
              {editingId ? '编辑客户分组' : '新建客户分组'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-3">
              {modalError && (
                <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                  {modalError}
                </div>
              )}

              <label className="block text-xs text-slate-700">
                分组名称 *
                <input
                  type="text"
                  className="field mt-1"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="例如：20260425渔具展、重点客户、代账公司客户"
                  maxLength="60"
                />
              </label>

              <label className="block text-xs text-slate-700">
                颜色
                <div className="mt-1 flex gap-2">
                  <input
                    type="color"
                    className="h-10 w-14 cursor-pointer"
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                  />
                  <input
                    type="text"
                    className="field flex-1"
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    placeholder="#3b82f6"
                    maxLength="7"
                  />
                </div>
              </label>

              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={handleModalClose}
                  className="btn-ghost text-sm"
                >
                  取消
                </button>
                <button type="submit" className="btn-primary text-sm">
                  {editingId ? '保存' : '创建'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
